import { Controller, useForm } from "react-hook-form";
import { HashTag, HumanAvatar, LetterIcon, MapPin, ProfileIcon, SuitcaseIconV2 } from "../../../../assets/icons";
import PhoneNumberInput from "../../../../common/components/new/phone-number/PhoneNumberInput";
import { SelectItem } from "../../../../common/components/new/select/Select";
import Input from "../../../../common/components/new/ui/Input";
import styles from "./waitListForm.module.scss";
import { useState } from "react";
import { usePostWaitlistMutation } from "../../../../service/waitlist-service";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import { toast } from "react-toastify";
import MultiSelect from "../../../../common/components/new/select/MultiSelect";
import CategorySelect from "../../../../common/components/new/select/CategorySelect";

interface FormType {
	fullName: string;
	phoneNumber: string;
	email: string;
	address: string;
	meterNumber: string;
	isBussiness: string;
}

const defaultValues = {
	fullName: "",
	phoneNumber: "",
	email: "",
	address: "",
	meterNumber: "",
	isBussiness: "",
};

type CategorySelection =
	| {
			category: string;
			subLabel: string;
			value: string;
	  }
	| undefined;

const WaitListForm = () => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [postWaitList, { isLoading }] = usePostWaitlistMutation();
	const items = [new SelectItem(1, "Solar Panel"), new SelectItem(2, "Battery"), new SelectItem(3, "Inverter")];
	const appliances = [
		{
			label: "1.5 KVA",
			subLabels: ["LED Lights", "Laptop", "Desktop Computer", "Ceiling Fans", "Small Refrigerator", "Router"],
			value: "1.5",
		},
		{
			label: "2.5 KVA",
			subLabels: ["LED Lights", "Air Conditioner (1.2 HP)", "Large Refrigerator", "TV", "Computer", "Water Pump"],
			value: "2.5",
		},
		{
			label: "3.5 KVA",
			subLabels: ["LED Lights", "Air Conditioner (1.8 HP)", "Refrigerator", "Washing Machine", "Microwave", "Multiple Fans"],
			value: "3.5",
		},
		{
			label: "5 KVA",
			subLabels: ["LED Lights", "Air Conditioner (2.4 HP)", "Deep Freezer", "Electric Oven", "Water Heater", "Heavy Appliances"],
			value: "5",
		},
	];

	const userTypes = [
		{
			label: "Individual",
			value: "individual",
			icon: <HumanAvatar />
		},
		{
			label: "Business",
			value: "business",
			icon: <SuitcaseIconV2/>
		}
	];

	const {
		handleSubmit,
		control,
		reset,
		formState: { errors, isDirty },
	} = useForm<FormType>({
		reValidateMode: "onChange",
		mode: "onTouched",
	});

	const toggleDialog = () => {
		setDialogOpen(!dialogOpen);
	};

	const clearForm = () => {
		reset(defaultValues);
	};

	const onSubmit = ({ fullName, phoneNumber, email, address, meterNumber, isBussiness }: FormType) => {
		let data = { fullName: fullName?.trim(), email: email?.trim(), address: address?.trim(), phoneNumber, meterNumber, isBussiness : isBussiness === "business" };

		postWaitList(data)
			.unwrap()
			.then(res => {
				toggleDialog();
				clearForm();
			})
			.catch(err => {
				toast.error(err?.data?.title ?? "Something went wrong");
			})
			.finally(() => {});
	};

	return (
		<>
			{dialogOpen && (
				<CommonDialog noPadding>
					<div className={styles.confirmModalBody}>
						<div className={styles.confirmBodyIcon}>
							<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M19.9999 0.0498047C9.09226 0.0498047 0.249878 8.89218 0.249878 19.7998C0.249878 30.7074 9.09226 39.5498 19.9999 39.5498C30.9075 39.5498 39.7499 30.7074 39.7499 19.7998C39.7499 8.89218 30.9075 0.0498047 19.9999 0.0498047ZM27.0713 15.8003C27.5893 15.1671 27.496 14.2339 26.8629 13.7159C26.2297 13.1978 25.2965 13.2912 24.7785 13.9243L16.9274 23.52L14.1348 20.7274C13.5563 20.1489 12.6184 20.1489 12.04 20.7274C11.4615 21.3059 11.4615 22.2437 12.04 22.8222L15.99 26.7722C16.2859 27.0681 16.6932 27.2251 17.1112 27.2042C17.5291 27.1834 17.9188 26.9867 18.1838 26.6628L27.0713 15.8003Z"
									fill="#F9A01B"
								/>
							</svg>
						</div>
						<div className={styles.textContainer}>
							<h3 className={styles.confirmBodyHeader}>
								We have added you to
								<br /> the waiting list
							</h3>
							<p className={styles.subText}>We will let you know when all is set</p>
						</div>
					</div>
					<div className={styles.modalFooter}>
						<button onClick={toggleDialog} className={styles.modalFooterButton}>
							Done
						</button>
					</div>
				</CommonDialog>
			)}
			<div className={styles.container}>
				<h2 className={styles.waitlistText}>Join Waitlist Now</h2>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Controller
						name="fullName"
						control={control}
						rules={{
							required: "Full name is required",
							pattern: {
								value: /^[A-Za-z\s]+$/,
								message: "Full name should only contain letters",
							},
							validate: value => {
								if (value.trim().length === 0) return "Full name cannot be empty";
								if (value.trim().length < 2) return "Invalid Full name";
								return true;
							},
						}}
						render={({ field: { onChange, value, ref } }) => (
							<Input
								styles={{
									inputContainer: styles.whiteBackground,
								}}
								required
								label="Full Name"
								placeholder="e.g Glory Faleye"
								error={errors.fullName?.message}
								inputProps={{
									onChange,
									value: value || "",
								}}
							>
								<ProfileIcon />
							</Input>
						)}
					/>
					<Controller
						name="phoneNumber"
						control={control}
						rules={{
							required: "Phone number is required",
							validate: value => {
								if (value.length < 13 || value.length > 14) return "Invalid phonenumber";
								return true;
							},
						}}
						render={({ field: { onChange, value, ref } }) => (
							<PhoneNumberInput
								backgroundColor="white"
								disableFlagSelector={false}
								value={value || ""}
								onChange={onChange}
								ref={ref}
								required
								error={isDirty ? errors.phoneNumber?.message : undefined}
								// error={touchedFields.phoneNumber || isSubmitted ? errors.phoneNumber?.message : undefined}
								placeholder="Enter phone number"
							/>
						)}
					/>

					<Controller
						name="email"
						control={control}
						rules={{
							required: "Email is required",
							pattern: {
								value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
								message: "Invalid email address",
							},
						}}
						render={({ field: { onChange, value, ref } }) => (
							<Input
								styles={{
									inputContainer: styles.whiteBackground,
								}}
								required
								label="Email"
								placeholder="Email"
								error={errors.email?.message}
								inputProps={{
									onChange,
									value: value || "",
									type: "email",
								}}
							>
								<LetterIcon />
							</Input>
						)}
					/>
					<Controller
						name="address"
						control={control}
						rules={{
							required: "Address is required",
						}}
						render={({ field: { onChange, value, ref } }) => (
							<Input
								styles={{
									inputContainer: styles.whiteBackground,
								}}
								required
								label="Address"
								placeholder="101, Isolo Road, Egbe, Alimosho"
								error={errors.address?.message}
								inputProps={{
									onChange,
									value: value || "",
								}}
							>
								<MapPin />
							</Input>
						)}
					/>
					<Controller
						name="meterNumber"
						control={control}
						rules={{
							required: "Customer meter number is required",
							pattern: {
								value: /^\d+$/,
								message: "Invalid meter number",
							},
						}}
						render={({ field: { onChange, value, ref } }) => (
							<Input
								styles={{
									inputContainer: styles.whiteBackground,
								}}
								required
								label="Customer Meter Number"
								placeholder="34879"
								error={errors.meterNumber?.message}
								inputProps={{
									onChange,
									value: value || "",
								}}
							>
								<HashTag />
							</Input>
						)}
					/>

					<Controller
						name="isBussiness"
						control={control}
						rules={{
							required: "Please select user type",
						}}
						render={({ field: { onChange, value } }) => {
							const selectedItem = value ? userTypes.find(item => item.value === value) : undefined;
							const selected = selectedItem ? { label: selectedItem.label, value: selectedItem.value } : undefined;

							return (
								<CategorySelect
									selected={selected}
									onChange={selected => {
										onChange(selected?.value ?? "");
									}}
									items={userTypes}
									label="Are you applying as an individual OR business?"
									placeholder="Select user type"
									error={errors.isBussiness?.message}
									required
								>
									<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path
											d="M15.7396 6.78622L10.7374 3.20504C10.4722 3.01513 10.3395 2.92018 10.1948 2.88346C10.067 2.85102 9.93313 2.85102 9.80534 2.88346C9.66063 2.92018 9.52799 3.01513 9.26272 3.20504L4.26048 6.78623M15.7396 6.78622L17.5209 8.06144M15.7396 6.78622V8.88794M4.26048 6.78623L2.47925 8.06144M4.26048 6.78623V13.998C4.26048 14.8847 4.26048 15.3281 4.43305 15.6668C4.58485 15.9647 4.82707 16.207 5.12499 16.3588C5.46369 16.5313 5.90706 16.5313 6.79381 16.5313H10.5938"
											stroke="#FF7926"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
										<path
											d="M15.5562 11.6006C15.5562 11.2131 15.081 11.0396 14.8411 11.3395L12.4669 14.3075C12.2515 14.5767 12.4388 14.9808 12.7789 14.9808H13.9438V16.7119C13.9438 17.0994 14.419 17.2729 14.6589 16.973L17.0331 14.005C17.2485 13.7358 17.0612 13.3317 16.7211 13.3317H15.5562V11.6006Z"
											fill="#FF7926"
										/>
										<path
											d="M7.823 10.0001C7.823 8.79771 8.79771 7.823 10.0001 7.823C11.2025 7.823 12.1772 8.79771 12.1772 10.0001C12.1772 11.2025 11.2025 12.1772 10.0001 12.1772C8.79771 12.1772 7.823 11.2025 7.823 10.0001Z"
											stroke="#FF7926"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</CategorySelect>
							);
						}}
					/>
					<button disabled={isLoading} className={styles.waitListButton}>
						{isLoading ? "Sending..." : "Join the waitlist"}
						<svg className={styles.svg} width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M8.5 1.3125L13.1875 5.99999L8.5 10.6875M12.625 5.99999H0.8125"
								stroke="#F9A01B"
								strokeWidth="1.13"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</form>
			</div>
		</>
	);
};
export default WaitListForm;
